import { create } from 'zustand';
import type { WeatherStation } from '../types/weatherStation';
import { createLogger } from '../utils/logger';
import { useTableStore } from './tableStore';
import { useMapStore } from './mapStore';
import { useNavigationStore } from './navigationStore';
import { useStationDataStore } from './stationDataStore';
import { useFilterStore } from './filterStore';
import {
  performanceMonitor,
  PERFORMANCE_TARGETS,
  monitorMarkerClick,
  endMarkerClick,
  monitorTableSync,
  endTableSync
} from '../utils/performanceMonitor';

const log = createLogger('AppStore');

// Re-export types for backward compatibility
export type { FilterState, CountryStateData, BoundaryData } from './filterStore';
export type { TableState } from './tableStore';
export type { MapStoreState as MapState } from './mapStore';

/**
 * Residual appStore: coordination layer for station selection, error/warning handlers,
 * and coordinate search view state. All domain data lives in dedicated stores.
 * Requirements: 19.1, 19.2, 19.3, 19.4, 19.5
 */

// AppState — residual state + mirrored fields from child stores for backward compat
export interface AppState {
  // Coordinates search view state
  isViewingStationDetails: boolean;
  expandedStationCodes: Set<string>;
  isInfoPanelVisible: boolean;

  // Error handling callbacks
  onError?: (message: string, actionLabel?: string, onAction?: () => void) => void;
  onWarning?: (message: string) => void;

  // Mirrored from navigationStore (backward compat — prefer useNavigationStore directly)
  activeSection: 'search' | 'availability' | 'statistics' | 'network' | 'obsQuality' | 'observations' | 'dtnEuQuality';
  isPanelCollapsed: boolean;

  // Mirrored from stationDataStore (backward compat — prefer useStationDataStore directly)
  allStations: WeatherStation[];
  isLoadingAllStations: boolean;
  selectedStation: WeatherStation | null;
  selectedStations: WeatherStation[];

  // Mirrored from filterStore (backward compat — prefer useFilterStore directly)
  filterState: import('./filterStore').FilterState;
  countryStateData: import('./filterStore').CountryStateData;
  filteredStations: WeatherStation[];
  filterError: string | null;
  isLoadingCountryData: boolean;
  boundariesLoading: boolean;
  boundaries: import('./filterStore').BoundaryData[];
}

export interface AppActions {
  // Delegated nav/station/filter accessors (backward compat)
  setActiveSection: (section: 'search' | 'availability' | 'statistics' | 'network' | 'obsQuality' | 'observations' | 'dtnEuQuality') => void;
  togglePanel: () => void;
  loadAllStations: () => Promise<void>;

  // Coordinate search view
  selectStationForDetails: (station: WeatherStation) => void;
  backToCoordinatesResults: () => void;
  toggleStationExpansion: (stationCode: string) => void;
  setExpandedStations: (stationCodes: Set<string>) => void;

  // Error/warning
  setErrorHandler: (handler: (message: string, actionLabel?: string, onAction?: () => void) => void) => void;
  setWarningHandler: (handler: (message: string) => void) => void;
  clearCacheAndReload: () => Promise<void>;

  // Country filtering (delegated to filterStore)
  loadCountryStateData: () => Promise<void>;
  applyFilter: (countries: Set<string>, states: Map<string, Set<string>>, cities: Map<string, Map<string, Set<string>>>) => Promise<void>;
  clearFilter: () => void;
  toggleFilterPanel: () => void;
  loadBoundariesAsync: (countries: Set<string>, states: Map<string, Set<string>>, cities: Map<string, Map<string, Set<string>>>) => Promise<void>;

  // Station selection coordination
  handleStationSelection: (station: WeatherStation, currentZoom: number) => void;
  handleMarkerClick: (station: WeatherStation, currentZoom: number) => void;
  handleTableRowClick: (station: WeatherStation, currentZoom: number) => void;
  setHighlightBordersMode: (enabled: boolean) => void;
}

export type AppStore = AppState & AppActions;

// --- Zoom action helper ---
interface ZoomActionResult {
  shouldZoom: boolean;
  shouldRecenter: boolean;
  targetZoom: number;
}

function determineZoomAction(currentZoom: number): ZoomActionResult {
  const TARGET_ZOOM = 12;
  if (currentZoom < TARGET_ZOOM) return { shouldZoom: true, shouldRecenter: false, targetZoom: TARGET_ZOOM };
  if (currentZoom === TARGET_ZOOM) return { shouldZoom: false, shouldRecenter: true, targetZoom: TARGET_ZOOM };
  return { shouldZoom: true, shouldRecenter: false, targetZoom: TARGET_ZOOM };
}

// --- Store ---
export const useAppStore = create<AppStore>((set, get) => ({
  // Residual state
  isViewingStationDetails: false,
  expandedStationCodes: new Set<string>(),
  isInfoPanelVisible: false,
  onError: undefined,
  onWarning: undefined,

  // Mirrored state (populated by subscriptions below)
  activeSection: 'search' as const,
  isPanelCollapsed: true,
  allStations: [],
  isLoadingAllStations: false,
  selectedStation: null,
  selectedStations: [],
  filterState: {
    selectedCountries: new Set<string>(),
    selectedStates: new Map<string, Set<string>>(),
    selectedCities: new Map<string, Map<string, Set<string>>>(),
    isFilterPanelOpen: false,
    searchQuery: '',
  },
  countryStateData: { countries: [], statesByCountry: new Map<string, string[]>() },
  filteredStations: [],
  filterError: null,
  isLoadingCountryData: false,
  boundariesLoading: false,
  boundaries: [],

  // --- Delegated getters (backward compat) ---
  // Components that destructure { activeSection, isPanelCollapsed } from useAppStore()
  // will now get them from a Zustand computed subscription via the proxy pattern below.

  // --- Delegated actions ---
  setActiveSection: (section) => useNavigationStore.getState().setActiveSection(section),
  togglePanel: () => useNavigationStore.getState().togglePanel(),
  loadAllStations: () => useStationDataStore.getState().loadAllStations(),

  // Error/warning handlers
  // ponytail: Keep backward-compat setErrorHandler/setWarningHandler for existing useToast consumers.
  // Additionally delegate to notificationStore so new system always receives notifications.
  setErrorHandler: (handler) => set({ onError: (message, actionLabel, onAction) => {
    handler(message, actionLabel, onAction);
    // Also emit to notification store for unified rendering
    import('../store/notificationStore').then(({ useNotificationStore }) => {
      useNotificationStore.getState().error(message, undefined, undefined, actionLabel, onAction);
    });
  }}),
  setWarningHandler: (handler) => set({ onWarning: (message) => {
    handler(message);
    import('../store/notificationStore').then(({ useNotificationStore }) => {
      useNotificationStore.getState().warn(message);
    });
  }}),

  // Coordinate search view
  selectStationForDetails: (station) => {
    useStationDataStore.getState().setSelectedStation(station);
    set({ isViewingStationDetails: true });
  },
  backToCoordinatesResults: () => {
    useStationDataStore.getState().setSelectedStation(null);
    set({ isViewingStationDetails: false });
  },
  toggleStationExpansion: (stationCode) => {
    set((state) => {
      const newExpanded = new Set(state.expandedStationCodes);
      if (newExpanded.has(stationCode)) newExpanded.delete(stationCode);
      else newExpanded.add(stationCode);
      return { expandedStationCodes: newExpanded };
    });
  },
  setExpandedStations: (stationCodes) => set({ expandedStationCodes: new Set(stationCodes) }),

  // Cache clear
  clearCacheAndReload: async () => {
    const { BrowserCache } = await import('../utils/cache');
    BrowserCache.clearAll();
    // Reset station data in the dedicated store
    useStationDataStore.setState({ allStations: [] });
  },

  // Country filtering — delegate to filterStore
  loadCountryStateData: () => useFilterStore.getState().loadCountryStateData(),
  applyFilter: (countries, states, cities) => useFilterStore.getState().applyFilter(countries, states, cities),
  clearFilter: () => useFilterStore.getState().clearFilter(),
  toggleFilterPanel: () => useFilterStore.getState().toggleFilterPanel(),
  loadBoundariesAsync: (countries, states, cities) => useFilterStore.getState().loadBoundariesAsync(countries, states, cities),

  // Station selection coordination (cross-store)
  handleStationSelection: (station, currentZoom) => {
    log.debug(`🎯 Station selection: ${station.stationCode} (current zoom: ${currentZoom})`);
    const zoomAction = determineZoomAction(currentZoom);

    useMapStore.getState().selectStation(
      station.stationCode,
      { lat: station.latitude, lon: station.longitude },
      zoomAction.targetZoom
    );

    // ponytail: suppress map popup on mobile/tablet — detail panel handles it instead
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) {
      window.dispatchEvent(new CustomEvent('openStationPopup', {
        detail: {
          stationCode: station.stationCode,
          station,
          shouldZoom: zoomAction.shouldZoom,
          shouldRecenter: zoomAction.shouldRecenter,
          currentZoom,
          targetZoom: zoomAction.targetZoom,
        }
      }));
    }
  },

  handleMarkerClick: (station, currentZoom) => {
    log.debug(`📍 Marker clicked: ${station.stationCode} (zoom: ${currentZoom})`);
    monitorMarkerClick(station.stationCode);

    get().handleStationSelection(station, currentZoom);

    monitorTableSync(station.stationCode);
    const tableStore = useTableStore.getState();
    tableStore.highlightRow(station.stationCode);
    tableStore.setScrollToStation(station.stationCode);
    endTableSync(station.stationCode);
    endMarkerClick(station.stationCode);
  },

  handleTableRowClick: (station, currentZoom) => {
    log.debug(`📋 Table row clicked: ${station.stationCode} (zoom: ${currentZoom})`);
    performanceMonitor.startTimer(`table-row-click-${station.stationCode}`);

    get().handleStationSelection(station, currentZoom);
    useTableStore.getState().highlightRow(station.stationCode);

    performanceMonitor.endTimer(
      `table-row-click-${station.stationCode}`,
      'Table Row Click',
      PERFORMANCE_TARGETS.MARKER_CLICK
    );
  },

  setHighlightBordersMode: (enabled) => useMapStore.getState().setHighlightBordersMode(enabled),
}));

// --- Backward-compat proxy ---
// Many components destructure { activeSection, isPanelCollapsed, selectedStation, allStations, ... }
// from useAppStore(). We sync those from dedicated stores so existing code doesn't break.
// ponytail: subscribe to child stores and mirror into appStore. Removes once consumers migrate.
useNavigationStore.subscribe((navState) => {
  // Expose nav state on appStore for backward compat (read-only mirror)
  (useAppStore as any).setState({
    activeSection: navState.activeSection,
    isPanelCollapsed: navState.isPanelCollapsed,
  });
});

useStationDataStore.subscribe((sdState) => {
  (useAppStore as any).setState({
    allStations: sdState.allStations,
    isLoadingAllStations: sdState.isLoadingAllStations,
    selectedStation: sdState.selectedStation,
    selectedStations: sdState.selectedStations,
  });
});

useFilterStore.subscribe((fState) => {
  (useAppStore as any).setState({
    filterState: fState.filterState,
    countryStateData: fState.countryStateData,
    filteredStations: fState.filteredStations,
    filterError: fState.filterError,
    isLoadingCountryData: fState.isLoadingCountryData,
    boundariesLoading: fState.boundariesLoading,
    boundaries: fState.boundaries,
  });
});

// Initialize mirrored state immediately
(useAppStore as any).setState({
  ...useNavigationStore.getState(),
  ...useStationDataStore.getState(),
  ...useFilterStore.getState(),
});

// ─── Re-exports: Domain Slices ──────────────────────────────────────────────
export { useNavigationStore } from './navigationStore';
export { useMapStore } from './mapStore';
export { useTableStore } from './tableStore';
export { useSearchStore } from './searchStore';
export { useFilterStore } from './filterStore';
export { useStationDataStore } from './stationDataStore';

export type { ActiveSection } from './navigationStore';
export type { MapStore, MapStoreState, BoundaryData as MapBoundaryData } from './mapStore';
export type { SearchStore } from './searchStore';
export type { FilterStore } from './filterStore';
export type { StationDataStore, StationDataState } from './stationDataStore';
